import { memo, MouseEvent, useCallback } from 'react';
import classnames from 'classnames';

import Url from 'types/Url';
import { InfoModal, Option as OptionType } from 'types/ProductOptionGroup';
import { VariantAttributeValue } from 'types/models/VariantAttributeValue';
import InfoIcon from 'assets/icons/info.svg';
import Modal from '@components/Modal';
import getVariantHref from 'utils/getVariantHref';
import createStyleVariables from 'utils/createStyleVariables';
import cssVar from 'utils/cssVar';

import InfoModalContent from './components/InfoModalContent';
import Option from './components/Option';
import OptionSelector from './components/OptionSelector';

import classes from './OptionGroup.module.scss';

type Props = {
  id: string;
  title: string;
  info?: string;
  infoModal?: InfoModal;
  collapsedItemsMinCount: number;
  maxItemsInRow: number;
  options: OptionType[];
  availableOptions: OptionType[];
  predictedOptions: OptionType[];
  selectedId?: string;
  onHoverOption: (
    groupId: string,
    variantId: number,
    variantAttributeValue?: VariantAttributeValue,
    hoverIn?: boolean
  ) => void;
  onSelectOption: (variantId: number, href: Url) => void;
};

const OptionGroup = ({
  id: currentGroupId,
  title,
  info,
  infoModal,
  collapsedItemsMinCount,
  maxItemsInRow,
  options,
  availableOptions,
  predictedOptions,
  selectedId,
  onHoverOption,
  onSelectOption,
}: Props): JSX.Element => {
  const optionsCount = options.length || 0;

  const onClick = useCallback(
    (variantId: number | undefined, href: Url) => (e: MouseEvent) => {
      if (variantId) {
        e.preventDefault();
        onSelectOption(variantId, href);
      }
    },
    [onSelectOption]
  );

  const onHover = useCallback(
    (variantId?: number, variantAttributeValue?: VariantAttributeValue, hoverIn?: boolean) => () =>
      variantId && onHoverOption(currentGroupId, variantId, variantAttributeValue, hoverIn),
    [currentGroupId, onHoverOption]
  );

  return (
    <>
      <div className={classes.header}>
        <div className={classes.title}>{title}</div>
        {(infoModal || info) && (
          <div>
            {infoModal ? (
              <Modal
                hideWhenClickOutside
                trigger={
                  <div className={classnames(classes.info, classes.isInfoLink)}>
                    {info}
                    <InfoIcon className={classes.infoIcon} height={12} width={12} fill={cssVar('activeColor')} />
                  </div>
                }
                size={Modal.size.small}
              >
                <InfoModalContent imageUrl={infoModal.imageUrl} title={infoModal.title} content={infoModal.content} />
              </Modal>
            ) : (
              <div className={classes.info}>{info}</div>
            )}
          </div>
        )}
      </div>
      {optionsCount >= collapsedItemsMinCount ? (
        <OptionSelector selectedOptionId={selectedId} options={options} onSelect={onSelectOption} />
      ) : (
        <div className={classes.options} style={createStyleVariables({ maxItemsInRow })}>
          {options.map((option) => {
            const { id, name, priceImpact, status, variantId, variantAttributeValue } = option;

            return (
              <div key={id} className={classes.option}>
                <Option
                  name={name}
                  priceImpact={priceImpact}
                  status={status}
                  isSelected={selectedId === id}
                  isFadedOut={
                    !!availableOptions.length && !availableOptions.find(({ id: groupId }) => groupId === id)?.variantId
                  }
                  hideInfo={!!availableOptions.length}
                  isPredictionActive={!!predictedOptions.find(({ id: groupId }) => groupId === id)?.variantId}
                  onClick={onClick(variantId, getVariantHref({ ...option, id: variantId }))}
                  onHoverIn={onHover(variantId, variantAttributeValue, true)}
                  onHoverOut={onHover(variantId, variantAttributeValue, false)}
                />
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default memo(OptionGroup);
